import { useEffect, useState } from "react";
import { Typography, CircularProgress, Box, Chip } from "@mui/material";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import classes from "./Conversations.module.css";
import EmptyView from "../../../Component/EmptyView/EmptyView";
import api from "../../../BrandOnBoarding/api";
import { SITE_SUGGESTIONS } from "../../../utils/constant";

const statusColors = {
  COMPLETED: "green",
  RETRY: "red",
  DEFAULT: "#FCD34D",
};


const getSiteName = (domain) => {
  const match = SITE_SUGGESTIONS.find((s) => s.value === domain);
  return match ? match.name : domain;
};

export default function ConversationHistory({ transactionId, requestId, getConversationData, activeConversationId, onAfterDelete }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;

  const getConversationHistory = async () => {
    try {
      setLoading(true);

      const result = await api.get(`${webSocketBaseURL}/conversation/${transactionId}/${requestId}`)
      setData(result?.data || []);
    } catch (error) {
      console.error("Failed to fetch conversation history", error);
    } finally {
      setLoading(false);
    }
  };

const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      const deletedItem = data.find(item => item.id === id);
      
      await api.delete(`${webSocketBaseURL}/conversation/${id}`);
      const result = await api.get(`${webSocketBaseURL}/conversation/${transactionId}/${requestId}`);
      const remainingData = result?.data || [];
      setData(remainingData);

      if (onAfterDelete) {
        onAfterDelete(remainingData, id, deletedItem); 
      }
    } catch (error) {
      console.error("Failed to delete conversation", error);
    } finally {
      setDeletingId(null);
    }
};

  useEffect(() => {
    getConversationHistory();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!data.length) {
    return <EmptyView message="No conversation history found." />;
  }

  const oldestConversationId = data.length > 0 ? data[data.length - 1].id : null;

  const formatDateTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    return date.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {data.map((item) => (
        <Box className={classes.historyCard}>
          <Box className={classes.historyRow}>
            {/* LEFT STRIP */}
            <Box
              style={{
                borderLeft: `4px solid ${statusColors[item.status] || statusColors.DEFAULT}`,
              }}
            />

            {/* MAIN CONTENT */}
            <Box className={classes.historyContent}>
              <Box className={classes.historyHeader}>
                <Typography className={classes.category}>
                  {item.category}
                </Typography>
              </Box>

              <Box className={classes.contextRow}>
                <span>
                  <strong>Persona:</strong> {item.persona}
                </span>
              </Box>

              {/* Site Suggestions — only render if targetDomains exists and has values */}
              {item.request?.targetDomains?.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 500,
                      color: "#111827",
                    }}
                  >
                    Site Suggestions:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {item.request.targetDomains.map((domain) => (
                      <Chip
                        key={domain}
                        label={getSiteName(domain)}
                        size="small"
                        sx={{
                          fontSize: "11px",
                          height: "22px",
                          backgroundColor: "#e0f0ff",
                          color: "#1565c0",
                          border: "1px solid #90caf9",
                          fontWeight: 500,
                          borderRadius: "6px",
                          "& .MuiChip-label": {
                            px: "8px",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Box className={classes.timeRow}>
                {formatDateTime(item?.created_at)}
              </Box>
            </Box>

            {/* ACTIONS */}
            <Box className={classes.actions}>
              <MUIIconButton size="small" onClick={() => getConversationData(item)}>
                <VisibilityOutlinedIcon fontSize="small" />
              </MUIIconButton>
              {/* {item.id !== oldestConversationId && ( */}
                <MUIIconButton
                  size="small"
                  onClick={() => handleDelete(item.id)}
                  className={`${classes.deleteIcon}`}
                  disabled={deletingId === item.id}
                >
                  {deletingId === item.id
                    ? <CircularProgress size={16} />
                    : <DeleteIcon fontSize="small"  />
                  }
                </MUIIconButton>
              {/* )} */}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}